// KPI içe aktarma sihirbazı — önizleme diff özeti, türetilmiş yeniden hesap, hata mesajları
// (kpi-import-wizard.ts'ten ayrıldı, güvenlik/UX taraması 2026-10-08, 600 satır sınırı; davranış
// birebir, ana modül re-export eder).

import {
  formatKpiNumber,
  type KpiImportGrid,
  type KpiResolvedCell,
  type KpiResolvedGrid,
  type KpiSetIndicator,
} from "@mahir/shared";
import type { KpiImportPlan, ZodIssueLike } from "../api/kpi-import";

// ─── Önizleme diff özeti / türetilmiş yeniden hesap ─────────────────────────

export interface DiffSummary {
  create: number;
  update: number;
  conflict: number;
  unchanged: number;
  skipDerived: number;
  skippedRows: number;
  skippedDerivedRows: number;
  /** P1-2 — erişim/C3 nedeniyle düşen satırlar (müşteri). */
  skippedAccessRows: number;
  /** P1-3 — birimi hedefe dönüştürülemediği için düşen satırlar. */
  skippedUnitRows: number;
  /** P1-3 — birim çevrimi uygulanan hücreler. */
  convertedCells: number;
  /** yazılacak = create + update + conflict (çakışma kuralına göre bir kısmı korunabilir) */
  toWrite: number;
  newIndicators: number;
}

export function summarizePlan(plan: KpiImportPlan): DiffSummary {
  const s: DiffSummary = {
    create: 0,
    update: 0,
    conflict: 0,
    unchanged: 0,
    skipDerived: 0,
    skippedRows: plan.skipped.length,
    skippedDerivedRows: plan.skipped.filter((r) => r.reason.startsWith("türetilmiş")).length,
    skippedAccessRows: plan.skipped.filter((r) => r.access !== undefined).length,
    skippedUnitRows: plan.skipped.filter((r) => r.unit?.kind === "incompatible").length,
    convertedCells: plan.convertedCells ?? 0,
    toWrite: 0,
    newIndicators: plan.newIndicatorCodes.length,
  };
  for (const c of plan.cells) {
    if (c.outcome === "create") s.create += 1;
    else if (c.outcome === "update") s.update += 1;
    else if (c.outcome === "conflict") s.conflict += 1;
    else if (c.outcome === "unchanged") s.unchanged += 1;
    else s.skipDerived += 1;
  }
  s.toWrite = s.create + s.update + s.conflict;
  return s;
}

/**
 * Çakışma kuralına göre gerçekten yazılacak hücre sayısı (B-11): "koru" yalnız ÇAKIŞAN (dolu)
 * hücreyi korur; boş/XXX/ölçülmüyor hücrelere giden güncellemeler her iki kuralda da yazılır.
 */
export function cellsToWrite(summary: DiffSummary, policy: "overwrite" | "keep_existing"): number {
  return policy === "overwrite" ? summary.toWrite : summary.create + summary.update;
}

export interface DerivedPreviewRow {
  key: string;
  code: string;
  label: string;
  unit: string | null;
  entityId: string;
  entityName: string;
  year: number;
  before: KpiResolvedCell | undefined;
  after: KpiResolvedCell;
  changed: boolean;
}

function cellSig(c: KpiResolvedCell | undefined): string {
  if (!c) return "empty";
  return `${c.status}|${c.value?.toString() ?? ""}|${c.text ?? ""}`;
}

/**
 * Etkilenen türetilmiş kodlar: aktif formülü yazılan kodlardan birine (geçişli olarak) atıf yapan
 * göstergeler. Formülü hiç girdisi değişmeyen türetilmiş satırlar listeye girmez (gürültü).
 */
export function affectedDerivedCodes(
  indicators: readonly Pick<
    KpiSetIndicator,
    "code" | "isDerived" | "formulas" | "activeFormulaId"
  >[],
  writtenCodes: ReadonlySet<string>,
): Set<string> {
  const refsOf = new Map<string, string[]>();
  for (const ind of indicators) {
    if (!ind.isDerived) continue;
    const f =
      ind.formulas.find((x) => x.id === ind.activeFormulaId) ??
      ind.formulas.find((x) => x.isActive);
    if (!f) continue;
    refsOf.set(ind.code, f.expression.match(/[A-Za-z_][A-Za-z0-9_]*/g) ?? []);
  }
  const affected = new Set<string>();
  let grew = true;
  while (grew) {
    grew = false;
    for (const [code, refs] of refsOf) {
      if (affected.has(code)) continue;
      if (refs.some((r) => writtenCodes.has(r) || affected.has(r))) {
        affected.add(code);
        grew = true;
      }
    }
  }
  return affected;
}

/**
 * Türetilmiş göstergelerin etkilenen hücreleri (B-10): overlay resolve çıktısında (recalculated)
 * türetilmiş olan ve `before` (mevcut grid) ile DEĞİŞEN hücreler; "HESAPLANAMADI → HESAPLANAMADI",
 * "XXX → XXX", "10 → 10" gibi değişmeyen satırlar listeye girmez — kart sayısı = tablo satırı.
 */
export function derivedPreviewRows(
  recalculated: KpiResolvedGrid,
  before: KpiResolvedGrid | null,
  writtenCodes?: ReadonlySet<string>,
): DerivedPreviewRow[] {
  const affected = writtenCodes
    ? affectedDerivedCodes(recalculated.indicators, writtenCodes)
    : null;
  const beforeByMetric = new Map((before?.indicators ?? []).map((i) => [i.metricId, i]));
  const entityName = new Map(recalculated.entities.map((e) => [e.id, e.name]));
  const out: DerivedPreviewRow[] = [];
  for (const ind of recalculated.indicators) {
    if (!ind.isDerived) continue;
    if (affected && !affected.has(ind.code)) continue;
    const prev = beforeByMetric.get(ind.metricId);
    for (const [entityId, byYear] of Object.entries(ind.cells)) {
      for (const year of recalculated.years) {
        const after = byYear[year];
        if (!after) continue;
        const b = prev?.cells[entityId]?.[year];
        const changed =
          cellSig(b) !== cellSig(after) && !(b === undefined && after.status === "empty");
        if (!changed) continue;
        out.push({
          key: `${ind.metricId}|${entityId}|${year.toString()}`,
          code: ind.code,
          label: ind.label,
          unit: ind.unit,
          entityId,
          entityName: entityName.get(entityId) ?? entityId,
          year,
          before: b,
          after,
          changed,
        });
      }
    }
  }
  return out.sort((a, b) => a.code.localeCompare(b.code) || a.year - b.year);
}

/** P1-3 — önizleme "Dosyada" hücresi çevrim notu: "16.558.932 kWh → GJ ×0,0036". */
export function convertedNote(c: {
  converted?: { fromUnit: string; toUnit: string; factor: number; original: number };
}): string | null {
  if (!c.converted) return null;
  const f = c.converted.factor;
  const factorText =
    f >= 1 ? formatKpiNumber(f) : f.toFixed(6).replace(/0+$/, "").replace(".", ",");
  return `${formatKpiNumber(c.converted.original)} ${c.converted.fromUnit} → ${c.converted.toUnit} ×${factorText}`;
}

/** 403/400 gövdesindeki satır etiketleri (P1-2 — "hangi satır?"). */
export function accessErrorRowLabels(details: unknown): string[] {
  if (!details || typeof details !== "object") return [];
  const rows = (details as { rows?: unknown }).rows;
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => (r && typeof r === "object" ? (r as { rowLabel?: unknown }).rowLabel : undefined))
    .filter((l): l is string => typeof l === "string");
}

/** Planın yazdığı, görünüm aralığı dışında kalan yıllar (kural 8 — Adım 4 notu). */
export function yearsOutsideView(plan: KpiImportPlan, viewYears: readonly number[]): number[] {
  const view = new Set(viewYears);
  return [...new Set(plan.cells.map((c) => c.year))]
    .filter((y) => !view.has(y))
    .sort((a, b) => a - b);
}

// ─── Hata mesajları ──────────────────────────────────────────────────────────

/** Zod konum mesajı: `mapping[3].targetCode — ...` (konum yoksa yalnız mesaj). */
export function formatIssues(issues: readonly ZodIssueLike[]): string[] {
  return issues.map((i) => {
    const path = i.path.reduce<string>(
      (acc, p) => (typeof p === "number" ? `${acc}[${p.toString()}]` : acc ? `${acc}.${p}` : p),
      "",
    );
    return path ? `${path} — ${i.message}` : i.message;
  });
}

/** Job durumu → sihirbaz adımı (taslaktan devam, D8). */
export function stepForJobStatus(status: string): 1 | 2 | 3 | 4 {
  if (status === "ready") return 2;
  if (status === "committed") return 4;
  return 1;
}

export function gridRowCount(grid: KpiImportGrid | null): number {
  return grid ? grid.sheets.reduce((n, s) => n + s.rows.length, 0) : 0;
}

// ─── Birim durumları (DR7) ───────────────────────────────────────────────────

export interface UnitStateCounts {
  converted: number;
  incompatible: number;
  unknown: number;
}

/** Önizleme sayacı: çevrilen hücre / uyumsuz satır (yazılmaz) / karşılaştırılamayan hücre. */
export function countUnitStates(plan: {
  cells: readonly { converted?: unknown; unitNote?: unknown }[];
  skipped: readonly { unit?: { kind: "incompatible" } }[];
}): UnitStateCounts {
  return {
    converted: plan.cells.filter((c) => c.converted !== undefined).length,
    incompatible: plan.skipped.filter((k) => k.unit?.kind === "incompatible").length,
    unknown: plan.cells.filter((c) => c.unitNote !== undefined).length,
  };
}

/**
 * Aktar düğmesi son eki (TODO-KPI-99): atlanacak uyumsuz satırlar + çevrim yapılmadan yazılacak
 * (karşılaştırılamayan) hücreler — ikisi de sıfırsa boş.
 */
export function commitUnitNote(
  counts: UnitStateCounts,
  copy: {
    commitSkipped: (n: number) => string;
    commitUnconverted: (n: number) => string;
  },
): string {
  const parts = [
    counts.incompatible > 0 ? copy.commitSkipped(counts.incompatible) : null,
    counts.unknown > 0 ? copy.commitUnconverted(counts.unknown) : null,
  ].filter((x): x is string => x !== null);
  return parts.length > 0 ? ` ${parts.join(" ")}` : "";
}

/** "×0,2778" için çarpan metni (convertedNote ile aynı yazım). */
export function factorText(f: number): string {
  return f >= 1 ? formatKpiNumber(f) : f.toFixed(6).replace(/0+$/, "").replace(".", ",");
}
